import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/api";

const MAX_TRIES = 12;

export default function PaymentResult({ cancelled = false }) {
  const [params] = useSearchParams();
  const [state, setState] = useState(cancelled ? "cancelled" : "checking");
  const tries = useRef(0);

  useEffect(() => {
    if (cancelled) return;
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setState("error");
      return;
    }
    let timer;
    const poll = async () => {
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.payment_status === "paid") return setState("paid");
        if (["failed", "expired"].includes(res.payment_status)) return setState("error");
      } catch (e) {
        return setState("error");
      }
      if (++tries.current >= MAX_TRIES) return setState("timeout");
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [cancelled, params]);

  const copy = {
    checking: { icon: Loader2, title: "Confirming your payment", body: "One moment — Stripe is settling up." },
    paid: { icon: CheckCircle2, title: "You're on Loop Premium", body: "Unlimited roadmaps across every domain are unlocked." },
    cancelled: { icon: XCircle, title: "Checkout cancelled", body: "No charge was made. You can upgrade whenever you like." },
    error: { icon: XCircle, title: "Payment didn't go through", body: "Nothing was charged. Try again from your profile icon." },
    timeout: { icon: Loader2, title: "Still processing", body: "This is taking longer than usual. Refresh in a minute." },
  }[state];

  const Icon = copy.icon;

  return (
    <div className="min-h-screen grid-lines flex items-center justify-center p-6" data-testid="payment-result">
      <div className="panel p-12 max-w-lg w-full rise">
        <Icon
          className={`h-8 w-8 ${state === "paid" ? "text-primary" : "text-muted-foreground"} ${
            state === "checking" || state === "timeout" ? "animate-spin" : ""
          }`}
          strokeWidth={1.6}
        />
        <h1 className="mt-7 font-display text-3xl tracking-tight leading-none" data-testid="payment-result-title">
          {copy.title}
        </h1>
        <p className="mt-5 text-sm text-muted-foreground leading-relaxed">{copy.body}</p>
        <Link
          to="/dashboard"
          data-testid="back-to-dashboard-link"
          className="mt-9 inline-block px-6 py-3 rounded-full bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors duration-200"
        >
          Back to my command centre
        </Link>
      </div>
    </div>
  );
}
